import React from 'react';
import { SiteSettings } from '../../types';
import ColorPicker from './ColorPicker';

type ContactField = 'contactEmail' | 'facebookUrl' | 'instagramUrl' | 'tikTokUrl' | 'pinterestUrl';

type ThemeColorField = 'textColor' | 'backgroundColor' | 'accentColor' | 'highlightColor';

interface SiteSettingsSectionProps {
    settings: SiteSettings;
    onColorChange: (field: ThemeColorField, value: string) => void;
    onImageUrlChange: (value: string) => void;
    onWelcomeHeroImageUrlChange?: (value: string) => void;
    onWelcomeHowItWorksPathChange?: (value: string) => void;
    onContactChange: (field: ContactField, value: string) => void;
    onAllCategoriesIconChange: (value: string) => void;
    onOpenPicker: (field: ThemeColorField) => void;
    editingField: ThemeColorField | null;
    tempColor: string;
    setTempColor: (value: string) => void;
    onPickerSave: (color: string) => void;
    onPickerCancel: () => void;
    onSave: () => void;
}

/**
 * Shop branding only (theme, hero, contact).
 * Tax / shipping / platform fees live under Admin → Marketplace economics.
 */
const SiteSettingsSection: React.FC<SiteSettingsSectionProps> = ({
    settings,
    onColorChange,
    onImageUrlChange,
    onWelcomeHeroImageUrlChange,
    onWelcomeHowItWorksPathChange,
    onContactChange,
    onAllCategoriesIconChange,
    onOpenPicker,
    editingField,
    tempColor,
    setTempColor,
    onPickerSave,
    onPickerCancel,
    onSave,
}) => {
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

    return (
        <section className="rounded-lg bg-white p-8 shadow-lg">
            <h2 className="mb-2 text-2xl font-bold text-gray-800">Website Settings</h2>
            <p className="mb-6 text-sm text-gray-500">
                Shop look &amp; feel, welcome banner, and contact links. Fees and tax are under{' '}
                <strong>Marketplace economics</strong> in the sidebar.
            </p>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                {/* Text Color */}
                <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Text Color</label>
                    <div className="flex gap-3">
                        <input
                            type="text"
                            value={settings.textColor}
                            onChange={(e) => onColorChange('textColor', e.target.value)}
                            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 uppercase focus:border-transparent focus:ring-2 focus:ring-blue-500"
                            placeholder="#334D3A"
                        />
                        <div
                            className="h-12 w-12 cursor-pointer rounded-lg border-2 border-gray-300 shadow"
                            style={{
                                backgroundColor: hexRegex.test(settings.textColor)
                                    ? settings.textColor
                                    : '#ccc',
                            }}
                            onClick={() => onOpenPicker('textColor')}
                        />
                    </div>
                </div>

                {/* Background Color */}
                <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Background Color</label>
                    <div className="flex gap-3">
                        <input
                            type="text"
                            value={settings.backgroundColor}
                            onChange={(e) => onColorChange('backgroundColor', e.target.value)}
                            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 uppercase focus:border-transparent focus:ring-2 focus:ring-blue-500"
                            placeholder="#F7F3EB"
                        />
                        <div
                            className="h-12 w-12 cursor-pointer rounded-lg border-2 border-gray-300 shadow"
                            style={{
                                backgroundColor: hexRegex.test(settings.backgroundColor)
                                    ? settings.backgroundColor
                                    : '#ccc',
                            }}
                            onClick={() => onOpenPicker('backgroundColor')}
                        />
                    </div>
                </div>

                {/* Accent Color */}
                <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Accent Color</label>
                    <div className="flex gap-3">
                        <input
                            type="text"
                            value={settings.accentColor}
                            onChange={(e) => onColorChange('accentColor', e.target.value)}
                            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 uppercase focus:border-transparent focus:ring-2 focus:ring-blue-500"
                            placeholder="#C9A86C"
                        />
                        <div
                            className="h-12 w-12 cursor-pointer rounded-lg border-2 border-gray-300 shadow"
                            style={{
                                backgroundColor: hexRegex.test(settings.accentColor)
                                    ? settings.accentColor
                                    : '#ccc',
                            }}
                            onClick={() => onOpenPicker('accentColor')}
                        />
                    </div>
                </div>

                {/* Highlight Color */}
                <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                        Highlight (buttons / focus)
                    </label>
                    <div className="flex gap-3">
                        <input
                            type="text"
                            value={settings.highlightColor || '#A67C52'}
                            onChange={(e) => onColorChange('highlightColor', e.target.value)}
                            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 uppercase focus:border-transparent focus:ring-2 focus:ring-blue-500"
                            placeholder="#A67C52"
                        />
                        <div
                            className="h-12 w-12 cursor-pointer rounded-lg border-2 border-gray-300 shadow"
                            style={{
                                backgroundColor: hexRegex.test(settings.highlightColor || '')
                                    ? settings.highlightColor
                                    : '#ccc',
                            }}
                            onClick={() => onOpenPicker('highlightColor')}
                        />
                    </div>
                </div>

                {/* Background image */}
                <div className="md:col-span-2">
                    <label className="mb-2 block text-sm font-medium text-gray-700">Background image URL</label>
                    <input
                        type="url"
                        value={settings.backgroundImageUrl || ''}
                        onChange={(e) => onImageUrlChange(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:ring-2 focus:ring-blue-500"
                        placeholder="https://…"
                    />
                </div>

                {/* Welcome hero */}
                <div className="md:col-span-3 mt-2 border-t border-gray-200 pt-6">
                    <h3 className="mb-4 text-lg font-semibold text-gray-800">Guest welcome banner</h3>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="md:col-span-2">
                            <label className="mb-2 block text-sm font-medium text-gray-700">
                                Welcome banner image URL
                            </label>
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                                <input
                                    type="url"
                                    value={settings.welcomeHeroImageUrl || ''}
                                    onChange={(e) => onWelcomeHeroImageUrlChange?.(e.target.value)}
                                    className="flex-1 rounded-lg border border-gray-300 px-4 py-2 focus:ring-2 focus:ring-blue-500"
                                    placeholder="https://dxxxx.cloudfront.net/…/hero.jpg"
                                />
                                <div
                                    className="h-28 w-full shrink-0 overflow-hidden rounded-lg border-2 border-gray-300 shadow sm:h-24 sm:w-44"
                                    style={{
                                        backgroundImage: settings.welcomeHeroImageUrl
                                            ? `url(${settings.welcomeHeroImageUrl})`
                                            : 'none',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        backgroundColor: '#f3f4f6',
                                    }}
                                    title="Preview"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700">
                                &quot;How does it work?&quot; link
                            </label>
                            <input
                                type="text"
                                value={settings.welcomeHowItWorksPath || '/how-it-works'}
                                onChange={(e) => onWelcomeHowItWorksPathChange?.(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:ring-2 focus:ring-blue-500"
                                placeholder="/faq"
                            />
                        </div>
                    </div>
                </div>

                {/* Category banner */}
                <div className="md:col-span-3 mt-2 border-t border-gray-200 pt-6">
                    <h3 className="mb-4 text-lg font-semibold text-gray-800">Category Banner</h3>
                    <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                            All Categories Icon
                        </label>
                        <input
                            type="text"
                            value={settings.allCategoriesIcon || ''}
                            onChange={(e) => onAllCategoriesIconChange(e.target.value)}
                            className="w-full max-w-xs rounded-lg border border-gray-300 px-4 py-2 text-2xl focus:border-transparent focus:ring-2 focus:ring-blue-500"
                            placeholder="🌿"
                        />
                    </div>
                </div>

                {/* Contact & social */}
                <div className="md:col-span-3 mt-2 border-t border-gray-200 pt-6">
                    <h3 className="mb-4 text-lg font-semibold text-gray-800">Contact &amp; Social</h3>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700">Contact Email</label>
                            <input
                                type="email"
                                value={settings.contactEmail || ''}
                                onChange={(e) => onContactChange('contactEmail', e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                                placeholder="support@example.com"
                            />
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700">Facebook URL</label>
                            <input
                                type="url"
                                value={settings.facebookUrl || ''}
                                onChange={(e) => onContactChange('facebookUrl', e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                                placeholder="https://facebook.com/..."
                            />
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700">Instagram URL</label>
                            <input
                                type="url"
                                value={settings.instagramUrl || ''}
                                onChange={(e) => onContactChange('instagramUrl', e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                                placeholder="https://instagram.com/..."
                            />
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700">TikTok URL</label>
                            <input
                                type="url"
                                value={settings.tikTokUrl || ''}
                                onChange={(e) => onContactChange('tikTokUrl', e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                                placeholder="https://tiktok.com/@..."
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="mb-2 block text-sm font-medium text-gray-700">Pinterest URL</label>
                            <input
                                type="url"
                                value={settings.pinterestUrl || ''}
                                onChange={(e) => onContactChange('pinterestUrl', e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                                placeholder="https://pinterest.com/..."
                            />
                        </div>
                    </div>
                </div>
            </div>

            <button
                type="button"
                onClick={onSave}
                className="mt-8 rounded-lg bg-white px-8 py-3 font-medium text-indigo-600 shadow transition-colors hover:bg-white"
            >
                Save branding
            </button>

            {editingField && (
                <ColorPicker
                    value={tempColor}
                    onChange={setTempColor}
                    onCancel={onPickerCancel}
                    onSave={onPickerSave}
                />
            )}
        </section>
    );
};

export default SiteSettingsSection;
